import { formatRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import BottomSheetContent from '../../../../../../../components/BottomSheetComponents/BottomSheetContent'
import { convertEpochToDate } from '../../../../../../../utils/utils'
import type { LumpsumRebalancePlansResponse } from '../../../api/rebalance.types'
import {
  isMissedGainsComparisonAvailable,
  MissedGainsComparisonBarGraph,
  MissedGainsInfoUnavailableState
} from './MissedGainsComparisonBarGraph'

type MissedGainsInfoBottomSheetProps = {
  onDismiss: () => void
  rebalancePlansData: LumpsumRebalancePlansResponse | undefined
}

export function MissedGainsInfoBottomSheet({
  onDismiss,
  rebalancePlansData
}: MissedGainsInfoBottomSheetProps) {
  const missedGains = rebalancePlansData?.missedGains ?? 0
  const planSnapshotDate = rebalancePlansData?.planSnapshotDate ?? null
  const currentWealth = getMissedGainsCurrentWealth(rebalancePlansData)
  const isComparisonAvailable = isMissedGainsComparisonAvailable({
    missedGains,
    baselineValue: currentWealth
  })
  const heading =
    missedGains > 0
      ? `${formatRupeeLabel({ rawAmount: missedGains })} missed gains`
      : 'Missed gains'

  return (
    <BottomSheetContent
      heading={heading}
      onDismiss={onDismiss}
      bottomSheetHeadClassName="!pt-8"
      bottomSheetHeadingClassName="!text-[22px] !text-[#232323] !leading-[130%] !font-[500] !mb-0"
      bottomSheetBodyClassName="!pt-4 !pb-0"
      bottomSheetCtaClassName="!pt-5"
    >
      {isComparisonAvailable ? (
        <>
          <div className="text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
            {'This means your portfolio could have earned '}
            {formatRupeeLabel({ rawAmount: missedGains })}
            {' more, had you invested the same into top-ranked funds instead. '}
            {planSnapshotDate != null ? (
              <span className="font-[500] text-[#232323]">
                {`Calculated as of ${convertEpochToDate(planSnapshotDate, '%_d %b %Y')}.`}
              </span>
            ) : null}
          </div>
          <div className="mt-6 h-64 w-full rounded-[12px] border border-[#F0F4F7] px-2 pt-4">
            <MissedGainsComparisonBarGraph
              regularValue={currentWealth}
              directValue={currentWealth + missedGains}
            />
          </div>
        </>
      ) : (
        <MissedGainsInfoUnavailableState />
      )}
    </BottomSheetContent>
  )
}

function getMissedGainsCurrentWealth(
  rebalancePlansData: LumpsumRebalancePlansResponse | undefined
): number {
  let currentWealth = 0

  if (!Array.isArray(rebalancePlansData?.categories)) {
    return currentWealth
  }

  rebalancePlansData.categories.forEach((category) => {
    const recommendations = [
      ...(Array.isArray(category?.sellRecommendations) ? category.sellRecommendations : []),
      ...(Array.isArray(category?.underReviewRecommendations) ? category.underReviewRecommendations : [])
    ]

    recommendations.forEach((recommendation) => {
      if ((recommendation?.missedGains ?? 0) <= 0) {
        return
      }

      currentWealth += recommendation?.currentValue ?? 0
    })
  })

  return currentWealth
}
